import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import { getUsers, createUser, deleteUser } from "../../services/UserService";
import DeleteConfirmModal from "../common/DeleteConfirmModal";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    role: 0,
  });

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, searchQuery]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers(page + 1, rowsPerPage, searchQuery);
      if (response && response.data) {
        setUsers(response.data);
        setTotalUsers(response.totalUsers || 0);
      }
    } catch (error) {
      toast.error("Lỗi khi tải danh sách user");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        fullName: user.fullName || "",
        email: user.email || "",
        password: "",
        role: user.role || 0,
        _id: user._id,
      });
    } else {
      setSelectedUser(null);
      setFormData({
        fullName: "",
        email: "",
        password: "",
        role: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      fullName: "",
      email: "",
      password: "",
      role: 0,
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.fullName || !formData.email) {
      toast.error("Vui lòng điền đầy đủ thông tin");
      return;
    }

    if (!selectedUser && !formData.password) {
      toast.error("Vui lòng nhập mật khẩu");
      return;
    }

    try {
      setLoading(true);
      await createUser(formData);
      toast.success(
        selectedUser ? "Cập nhật user thành công" : "Tạo user thành công"
      );
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Có lỗi xảy ra");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setOpenDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await deleteUser(selectedUser._id);
      toast.success("Xóa user thành công");
      setOpenDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Có lỗi xảy ra khi xóa user");
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 1:
        return "Admin";
      case 2:
        return "CTV";
      default:
        return "User";
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 1:
        return "error";
      case 2:
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a202c" }}>
          Quản lý User
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: "#1976d2",
            padding: "10px 24px",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Thêm User
        </Button>
      </Box>

      <Paper
        elevation={1}
        sx={{
          overflow: "hidden",
        }}
      >
        <Box sx={{ padding: 3 }}>
          <TextField
            fullWidth
            placeholder="Tìm kiếm user theo email, tên..."
            value={searchQuery}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#666" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              marginBottom: 3,
            }}
          />

          {loading && users.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 8,
              }}
            >
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Alert severity="info">Không tìm thấy user nào</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f8f9fa",
                      "& th": {
                        fontWeight: 700,
                        color: "#1a202c",
                        borderBottom: "2px solid #e2e8f0",
                      },
                    }}
                  >
                    <TableCell>STT</TableCell>
                    <TableCell>Tên</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Vai trò</TableCell>
                    <TableCell>Ngày tạo</TableCell>
                    <TableCell align="center">Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow
                      key={user._id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "& td": {
                          borderBottom: "1px solid #e2e8f0",
                        },
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {user.fullName}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={getRoleLabel(user.role)}
                          color={getRoleColor(user.role)}
                          size="small"
                          sx={{
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString("vi-VN")
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleOpenDialog(user)}
                          sx={{
                            color: "#1976d2",
                            marginRight: 1,
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.1)",
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(user)}
                          sx={{
                            color: "#ef4444",
                            "&:hover": {
                              backgroundColor: "rgba(239, 68, 68, 0.1)",
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: "1px solid #e2e8f0",
              "& .MuiTablePagination-toolbar": {
                padding: "16px",
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "24px",
            color: "#1a202c",
            borderBottom: "1px solid #e2e8f0",
            padding: "24px",
          }}
        >
          {selectedUser ? "Chỉnh sửa User" : "Thêm User mới"}
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              fullWidth
              label="Họ và tên"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={!!selectedUser}
            />
            <TextField
              fullWidth
              label="Mật khẩu"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required={!selectedUser}
              helperText={
                selectedUser ? "Để trống nếu không muốn thay đổi mật khẩu" : ""
              }
            />
            <FormControl fullWidth>
              <InputLabel>Vai trò</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                label="Vai trò"
              >
                <MenuItem value={0}>User</MenuItem>
                <MenuItem value={1}>Admin</MenuItem>
                <MenuItem value={2}>CTV</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            gap: 2,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#666",
            }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : selectedUser ? (
              "Cập nhật"
            ) : (
              "Tạo mới"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmModal
        open={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Xóa User"
        message={`Bạn có chắc chắn muốn xóa user "${selectedUser?.fullName}"?`}
        loading={loading}
      />
    </Box>
  );
};

export default UserManagement;
